import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';

import { Button, Modal, Input, Checkbox, Typography, Alert, AutoComplete, notification } from 'antd';

import staffService from '../../service';
import { AddStaffModalProps, StaffByUserId, FormValues } from '../../types';

const AddStaffModal = ({ open, onCancel, eventId, setIsModalOpen, onSuccess }: AddStaffModalProps) => {
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isValid },
    watch
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      email: '',
      name: '',
      consent: false
    }
  });

  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<{ value: string; label: string; name: string }[]>([]);
  const [staffList, setStaffList] = useState<StaffByUserId[]>([]);
  const [errorMessage, setErrorMessage] = useState(null);

  const email = watch('email');
  const name = watch('name');
  const consent = watch('consent');

  const isSubmitDisabled = !isValid || !email || !name || name.trim().length < 3 || !consent;

  useEffect(() => {
    setErrorMessage(null);
  }, [email]);

  useEffect(() => {
    if (open) {
      const result = staffService.getStaffByUserId().subscribe(res => setStaffList(res.event_staff));

      return () => {
        result.unsubscribe();
      };
    }
  }, [open]);

  const handleSearch = (value: string) => {
    const filtered = staffList
      .filter(staff => staff.email.toLowerCase().includes(value.toLowerCase()))
      .map(staff => ({
        value: staff.email,
        label: `${staff.name} (${staff.email})`,
        name: staff.name
      }));
    setOptions(filtered);
  };

  const handleSelect = (email: string) => {
    const selected = staffList.find(staff => staff.email === email);

    if (selected) {
      setValue('email', selected.email, { shouldValidate: true });
      setValue('name', selected.name, { shouldValidate: true });
    }
  };

  const onSubmitForm = (data: FormValues) => {
    const payload = {
      ...data,
      event_id: eventId
    };
    setLoading(true);
    staffService.addStaff(payload).subscribe({
      next: () => {
        notification.success({ message: 'Credenciador adicionado!' });
        setLoading(false);
        setIsModalOpen(false);
        setErrorMessage(null);
        reset();
        onSuccess();
      },
      error: error => {
        setLoading(false);

        if (error?.data?.details.includes('em uso pelo produtor')) {
          setErrorMessage(
            <>
              Este é seu e-mail de acesso. Adicione os e-mails dos credenciadores que farão o check-in. Saiba mais na{' '}
              <a
                style={{ color: '#000', textDecoration: 'underline' }}
                href='https://ajuda.eduzz.com/hc/pt-br/search?query=blinket'
                target='_blank'
                rel='noopener noreferrer'
              >
                Central de Ajuda da Eduzz
              </a>
              .
            </>
          );
        } else if (error?.data?.details.includes('e-mail já está cadastrado')) {
          setErrorMessage('Este e-mail de credenciador já foi adicionado a este evento.');
        } else {
          setErrorMessage(error?.data?.details);
        }
      }
    });
  };

  const handleOnCancel = () => {
    setErrorMessage(null);
    reset();
    onCancel();
  };

  return (
    <Modal
      width={572}
      height={576}
      title='Adicionar credenciadores para equipe de check-in'
      open={open}
      onCancel={handleOnCancel}
      afterClose={() => {
        reset();
        setStaffList([]);
      }}
      footer={[
        <Button key='cancel' onClick={handleOnCancel}>
          Cancelar
        </Button>,
        <Button
          key='submit'
          type='primary'
          loading={loading}
          onClick={handleSubmit(onSubmitForm)}
          disabled={isSubmitDisabled}
        >
          Adicionar Credenciador
        </Button>
      ]}
    >
      <Typography.Paragraph>
        Convide por e-mail os credenciadores que farão a leitura dos QR Codes dos ingressos nos dias do evento, após
        essa data o acesso será revogado.
      </Typography.Paragraph>

      <form onSubmit={handleSubmit(onSubmitForm)}>
        <div style={{ marginBottom: 15 }}>
          <label>Email</label>
          <Controller
            name='email'
            control={control}
            rules={{
              required: 'Digite um e-mail válido para o convite',
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                message: 'Digite um e-mail válido'
              }
            }}
            render={({ field }) => (
              <AutoComplete
                {...field}
                options={options}
                onSearch={handleSearch}
                onSelect={value => {
                  field.onChange(value);
                  handleSelect(value);
                }}
                placeholder='Digite o email para o convite'
              />
            )}
          />
          {errors.email && <div style={{ color: 'red', fontSize: 12 }}>{errors.email.message}</div>}
        </div>

        {errorMessage && (
          <Alert style={{ fontSize: 14, marginBottom: 15 }} showIcon message={errorMessage} type='error' />
        )}

        <div style={{ marginBottom: 15 }}>
          <label>Nome do credenciador</label>
          <Controller
            name='name'
            control={control}
            rules={{
              required: 'Digite o nome do profissional',
              minLength: { value: 3, message: 'O nome deve ter pelo menos 3 caracteres' }
            }}
            render={({ field }) => <Input {...field} placeholder='Nome do profissional' />}
          />
          {errors.name && <div style={{ color: 'red', fontSize: 12 }}>{errors.name.message}</div>}
        </div>

        <Controller
          name='consent'
          control={control}
          rules={{
            required: 'Você deve aceitar os termos para continuar',
            validate: value => value === true || 'Você deve aceitar os termos para continuar'
          }}
          render={({ field }) => (
            <Checkbox checked={field.value} onChange={e => field.onChange(e.target.checked)}>
              Concordo que, ao adicionar este credenciador, ele poderá acessar os dados dos participantes que realizaram
              check-in com ele, como nome, e-mail e CPF.
            </Checkbox>
          )}
        />
        {errors.consent && <div style={{ color: 'red', fontSize: 12 }}>{errors.consent.message}</div>}
      </form>
    </Modal>
  );
};

export default AddStaffModal;
